/**
 * M&B Club Minicart Widget V2
 * Same behavior as mb-club-minicart.js (V1) with the new design, placed in the side cart footer above the subtotal.
 * Defined only for visitors the M&B Club A/B test assigns to it (mb-club-experiment.js).
 * Every global here is V2-specific so both versions can be loaded on the same page for the A/B test.
 */
const MB_CLUB_MINICART_V2_VERSION = 'v2';

let mbClubV2SubscriptionAddInFlight = false;

// Tracks the last visibility value pushed to the dataLayer so we only push on change.
let mbClubV2LastReportedWidgetVisible = null;

const MB_CLUB_MINICART_V2_CHECK_ICON = `<svg width="10" height="10" viewBox="0 0 10 10" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M2 5.2 4.1 7.3 8 3"/></svg>`;
const MB_CLUB_MINICART_V2_CROSS_ICON = `<svg width="10" height="10" viewBox="0 0 10 10" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" aria-hidden="true" focusable="false"><path d="M2 2l6 6M8 2l-6 6"/></svg>`;
const MB_CLUB_MINICART_V2_CARET_ICON = `<svg class="mb-club-minicart-v2__caret" width="8" height="8" viewBox="0 0 8 8" fill="currentColor" aria-hidden="true" focusable="false"><path d="M0.5 2h7L4 6.5z"/></svg>`;

/**
 * Pushes the member widget visibility to the GTM dataLayer as `member_widget_visible`.
 * Only pushes when the value changes to avoid spamming the dataLayer on every cart re-render.
 * @param {boolean} isVisible - Whether the MB Club / VIP membership widget is currently shown.
 */
function mbClubV2ReportWidgetVisible(isVisible) {
  const value = !!isVisible;
  if (mbClubV2LastReportedWidgetVisible === value) return;
  mbClubV2LastReportedWidgetVisible = value;
  try {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ member_widget_visible: value });
  } catch (error) {
    console.warn('[MB Club Minicart V2] Failed to push member_widget_visible:', error);
  }
}

/**
 * Returns the visitor's current Intelligems experiment assignments, keyed by experiment id.
 * Returns an empty object if Intelligems isn't loaded/ready, so it never blocks add to cart.
 */
function mbClubV2GetIntelligemsAssignments() {
  try {
    const user = window.igData?.user;
    if (!user?.getTestGroup) return {};
    const toAssignment = (experimentName, testGroup) => ({
      experiment: experimentName,
      group: testGroup?.name ?? '(no assignment)',
      testGroupId: testGroup?.id ?? null,
    });
    const assignments = Object.fromEntries(
      (user.getExperiments?.() ?? []).map(experiment => [
        experiment.id,
        toAssignment(experiment.name, user.getTestGroup(experiment.id)),
      ])
    );
    // getExperiments() only returns experiments targeted to the current page, but the
    // membership can be added from any page, so the M&B Club A/B test is read directly.
    const mbClubExperimentId = window.mbClubExperiment?.experimentId;
    if (mbClubExperimentId && !assignments[mbClubExperimentId]) {
      const mbClubTestGroup = user.getTestGroup(mbClubExperimentId);
      if (mbClubTestGroup) {
        assignments[mbClubExperimentId] = toAssignment(null, mbClubTestGroup);
      }
    }
    return assignments;
  } catch (error) {
    console.warn('[MB Club Minicart V2] Failed to read Intelligems assignments:', error);
    return {};
  }
}

class MBClubMinicartV2 extends HTMLElement {
  constructor() {
    super();
    // The shopper's last open/closed choice wins; otherwise use the V2 "open by default" setting.
    const localStorageOpen = localStorage.getItem('mb_club_minicart_v2_open');
    this.isOpen = localStorageOpen !== null ? localStorageOpen === 'true' : window.theme?.mb_club_v2_default_open === true;
    this.isLoading = false;
    this.isRendered = false;
    this.selectedOption = 'nonmember'; // 'vip' | 'nonmember'
    this.uid = `mb-club-minicart-v2-${Math.random().toString(36).slice(2, 8)}`;
    this.globalThisVariables();
  }
  globalThisVariables() {
    // Shared with V1 (theme settings > M&B Club Minicart Widget)
    this.exclusiveCountries = window.theme?.mb_club_exclusive_countries;
    this.mbClubSubscriptionProductId = window.theme?.mb_club_subscription_product_id;
    this.mbClubSubscriptionProductVariantId = window.theme?.mb_club_subscription_product_variant_id;
    this.mbClubSubscriptionProductSellingPlanId = window.theme?.mb_club_subscription_product_selling_plan_id;
    this.mbClubDiscountPercent = window.theme?.mb_club_discount_percent;
    this.mbClubSubscriptionProductPrice = window.theme?.mb_club_subscription_product_price;
    this.mbClubSubscriptionAllowedProductTypes = window.theme?.mb_club_subscription_allowed_product_types;
    // Own V2 settings (theme settings > M&B Club Minicart Widget V2)
    this.mbClubSubscriptionProductDefaultSelected = window.theme?.mb_club_v2_subscription_product_default_selected === true;
  }
  /**
   * Cart items from the JSON script tag that side-cart.liquid renders and side-cart.js keeps updated.
   * @returns {Array|null} The cart items, or null when the tag is missing or unreadable.
   */
  getCartItems() {
    const cartItemsEl = document.getElementById('mb-club-minicart-cart-items');
    if (!cartItemsEl) return null;
    try {
      const cartItems = JSON.parse(cartItemsEl.textContent);
      return Array.isArray(cartItems) ? cartItems : null;
    } catch (e) {
      console.log('[mb-club-minicart-v2] failed to parse cart items JSON', e);
      return null;
    }
  }
  syncSubscriptionAddedFromCart() {
    const cartItems = this.getCartItems();
    this.isSubscriptionAdded = cartItems
      ? cartItems.some(item => item.product_id == this.mbClubSubscriptionProductId)
      : this.getAttribute('is-subscription-added') === 'true';
  }
  setVipMemberStatus(status, meta = {}) {
    try {
      localStorage.setItem('mb-vip-member-status', JSON.stringify({
        status,
        ...meta,
        widgetVersion: MB_CLUB_MINICART_V2_VERSION,
        updatedAt: Date.now()
      }));
      console.log('[mb-club-minicart-v2] mb-vip-member-status ->', status, meta);
    } catch (error) {
      console.warn('[MB Club Minicart V2] Failed to save vip member status to localStorage:', error);
    }
    // Fire the trigger in the same tick the status is decided, so a GTM tag reading
    // localStorage off this event always sees the value we just wrote.
    console.log('[mb-club-minicart-v2] widget_membership_trigger firing. localStorage snapshot:', {
      'vip-ig-experiment': localStorage.getItem('vip-ig-experiment'),
      'mb-vip-member-status': localStorage.getItem('mb-vip-member-status'),
    });
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ 'event': 'widget_membership_trigger', 'mb_club_widget_version': MB_CLUB_MINICART_V2_VERSION });
  }
  /**
   * Reports the widget's actual visibility (connected to the DOM and not hidden) to the dataLayer.
   */
  reportVisibility() {
    mbClubV2ReportWidgetVisible(this.isConnected && !this.classList.contains('hidden'));
  }
  /**
   * Hides the widget when a check blocks it, so a previous render never stays on screen.
   */
  block() {
    this.isRendered = false;
    this.classList.add('hidden');
    mbClubV2ReportWidgetVisible(false);
  }
  addSubscriptionProductToCart(from = false) {
    return new Promise((resolve, reject) => {
      try {
        const properties = {
          _added_from_mini_cart: true,
          _hide_on_cart: true,
          _mb_club_widget_version: MB_CLUB_MINICART_V2_VERSION
        };
        // If Intelligems isn't ready yet, the property is omitted rather than delaying the add to cart.
        const igAssignments = mbClubV2GetIntelligemsAssignments();
        if (Object.keys(igAssignments).length) {
          // Line item property values must be strings, so the assignments are stored as JSON.
          properties._ig_experiments = JSON.stringify(igAssignments);
        } else {
          console.warn('[MB Club Minicart V2] No Intelligems assignments found, _ig_experiments omitted');
        }
        fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id: this.mbClubSubscriptionProductVariantId,
            quantity: 1,
            selling_plan: this.mbClubSubscriptionProductSellingPlanId,
            properties
          })
        })
        .then(response => {
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
          return response.json();
        })
        .then(data => {
          this.setAttribute('is-subscription-added', 'true');
          localStorage.setItem('mb_club_minicart_disabled', 'false');
          this.isSubscriptionAdded = true;
          resolve(data);
        })
        .catch(error => {
          console.error('Error adding subscription product to cart:', error);
          reject(error);
        });
      } catch (error) {
        console.error('Error in addSubscriptionProductToCart:', error);
        reject(error);
      }
    });
  }
  async removeSubscriptionProductFromCart() {
    try {
      // Use /cart/update.js with variant_id to set quantity to 0
      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          updates: {
            [this.mbClubSubscriptionProductVariantId]: 0
          }
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      localStorage.setItem('mb_club_minicart_disabled', 'true');
      this.setAttribute('is-subscription-added', 'false');
      this.isSubscriptionAdded = false;

      // Check if Shopify returned an error in the response
      if (data.errors) {
        throw new Error(data.errors);
      }

      return data;
    } catch (error) {
      console.error('Error in removeSubscriptionProductFromCart:', error);
      throw error;
    }
  }
  /**
   * Adds the membership automatically when "default selected" is on and the shopper hasn't opted out.
   * @returns {boolean} Whether an automatic add was started, so the render can show VIP selected right away.
   */
  evaluateIfdefaultAdded() {
    const wasDisabled = localStorage.getItem('mb_club_minicart_disabled') === 'true';

    // Only auto-add if cart already has at least 1 non-subscription item.
    // This prevents Elevar from tracking the auto-add as a user-initiated add-to-cart event.
    const cartItems = this.getCartItems();
    const hasRealCartItems = !!cartItems && cartItems.some(
      item => item.product_id != this.mbClubSubscriptionProductId && !item.properties?._buy_now
    );

    if (this.mbClubSubscriptionProductDefaultSelected && !this.isSubscriptionAdded && !wasDisabled && hasRealCartItems && !mbClubV2SubscriptionAddInFlight) {
      mbClubV2SubscriptionAddInFlight = true;
      this.addSubscriptionProductToCart("evaluateIfdefaultAdded")
        .then(() => {
          this.isSubscriptionAdded = true;
          this.refreshSideCart();
        })
        .catch(error => {
          mbClubV2SubscriptionAddInFlight = false;
          console.error('Error adding default subscription product:', error);
          this.selectedOption = 'nonmember';
          if (this.isRendered) this.updateSelectedState();
        });
      return true;
    }
    return false;
  }
  connectedCallback() {
    // side-cart.js re-inserts this element on every cart render, so the cart is read again each time.
    this.syncSubscriptionAddedFromCart();

    // Don't render if customer is already a member
    if (window.theme?.mb_club_customer_is_member === true) {
      if (this.isSubscriptionAdded) {
        console.log('[mb-club-minicart-v2] NOT rendering: customer is already a member, removing subscription product from cart');
        // Status update: already a member and had the subscription in cart, so we just cleaned it up.
        this.setVipMemberStatus('blocked_already_member', { subscriptionRemoved: true });
        this.removeSubscriptionProductFromCart();
      } else {
        console.log('[mb-club-minicart-v2] NOT rendering: customer is already a member');
        // Status update: already a member, nothing to clean up.
        this.setVipMemberStatus('blocked_already_member', { subscriptionRemoved: false });
      }
      this.block();
      return;
    }
    // Don't render if customer is not in exclusive countries
    if (this.exclusiveCountries && !this.exclusiveCountries.includes(window.Shopify.country)) {
      console.log('[mb-club-minicart-v2] NOT rendering: shopper country', window.Shopify.country, 'is not in exclusive countries list', this.exclusiveCountries);
      // Status update: shopper's country isn't on the exclusive-countries allow-list.
      this.setVipMemberStatus('blocked_country', { country: window.Shopify.country });
      this.block();
      return;
    }

    if (this.mbClubSubscriptionAllowedProductTypes?.trim()) {
      // If the widget was already activated in a previous render (qualifying product was found
      // and the subscription was added), keep showing it even if that product was later removed.
      // Shared with V1 on purpose: it records the shopper's cart history, not the widget design.
      const wasAlreadyActivated = localStorage.getItem('mb_club_widget_activated') === 'true';

      if (!wasAlreadyActivated) {
        const allowedTypes = this.mbClubSubscriptionAllowedProductTypes.split(',').map(t => t.trim().toLowerCase()).filter(Boolean);
        const cartItems = this.getCartItems();
        const hasAllowedItem = !!cartItems && cartItems.some(
          item => item.product_id != this.mbClubSubscriptionProductId &&
                  allowedTypes.includes((item.product_type || '').toLowerCase())
        );

        if (!hasAllowedItem) {
          console.log('[mb-club-minicart-v2] NOT rendering: no cart item matches allowed product types', allowedTypes);
          // Status update: cart has no item matching the allowed product types, and the widget
          // was never activated before (no sticky localStorage flag), so it's still gated.
          this.setVipMemberStatus('blocked_no_qualifying_product', { allowedTypes });
          this.block();
          return;
        }
        // Mark as activated so the widget persists even if the qualifying product is removed
        console.log('[mb-club-minicart-v2] qualifying product type found, activating widget (sticky via localStorage)');
        localStorage.setItem('mb_club_widget_activated', 'true');
      } else {
        console.log('[mb-club-minicart-v2] widget already activated previously (localStorage), skipping product-type check');
      }
    }
    // No A/B check here: the M&B Club A/B test only defines this widget for visitors assigned to V2.
    // Rendering waits a microtask so side-cart.js has set the new cart attributes (data-subtotal) first.
    Promise.resolve().then(() => {
      if (!this.isConnected) {
        console.log('[mb-club-minicart-v2] NOT rendering: element was disconnected from DOM before it could render');
        // Status update: the element was removed/replaced in the DOM (e.g. side-cart re-rendered)
        // before it rendered.
        this.setVipMemberStatus('blocked_disconnected');
        this.block();
        return;
      }

      console.log('[mb-club-minicart-v2] rendering widget: all conditions passed');
      // Status update: every gate passed (membership, country, product type), widget is about to render.
      this.setVipMemberStatus('rendered');
      this.render();
      this.updateVisibilityFromSubtotal();
    });
  }
  formatMembershipPrice() {
    const price = Number(this.mbClubSubscriptionProductPrice) || 0;
    return `$${Number.isInteger(price) ? price : price.toFixed(2)}`;
  }
  benefitRow(label, note, vipContent) {
    return `
      <tr>
        <th scope="row" class="mb-club-minicart-v2__benefit">
          ${label}
          ${note ? `<span class="mb-club-minicart-v2__benefit-note">${note}</span>` : ''}
        </th>
        <td class="mb-club-minicart-v2__cell" data-option="vip">${vipContent}</td>
        <td class="mb-club-minicart-v2__cell" data-option="nonmember">
          <span class="mb-club-minicart-v2__cross">${MB_CLUB_MINICART_V2_CROSS_ICON}</span><span class="visually-hidden">Not included</span>
        </td>
      </tr>`;
  }
  render() {
    const autoAddStarted = this.evaluateIfdefaultAdded();
    this.selectedOption = (this.isSubscriptionAdded || autoAddStarted) ? 'vip' : 'nonmember';

    const percent = this.mbClubDiscountPercent;
    const price = this.formatMembershipPrice();
    const included = `<span class="mb-club-minicart-v2__check">${MB_CLUB_MINICART_V2_CHECK_ICON}</span><span class="visually-hidden">Included</span>`;

    this.innerHTML = `
      <section class="mb-club-minicart-v2" aria-labelledby="${this.uid}-title">
        <div class="mb-club-minicart-v2__loading-overlay hidden">
          <div class="mb-club-minicart-v2__loading-spinner"></div>
        </div>
        <div class="mb-club-minicart-v2__top">
          <div class="mb-club-minicart-v2__intro">
            <p class="mb-club-minicart-v2__title" id="${this.uid}-title">Save ${percent}% on every order</p>
            <p class="mb-club-minicart-v2__subtitle">M&amp;B Club — First month free, then ${price}/mo.</p>
          </div>
          <button type="button" class="mb-club-minicart-v2__cta"></button>
        </div>
        <div class="mb-club-minicart-v2__panel" id="${this.uid}-benefits">
          <div class="mb-club-minicart-v2__panel-inner">
            <div class="mb-club-minicart-v2__panel-content">
              <div class="mb-club-minicart-v2__panel-scroll">
                <table class="mb-club-minicart-v2__table">
                  <caption class="visually-hidden">M&amp;B Club member benefits compared with non-member</caption>
                  <thead>
                    <tr>
                      <th scope="col" class="mb-club-minicart-v2__benefit-head">Benefit</th>
                      <th scope="col" class="mb-club-minicart-v2__option-head" data-option="vip">
                        <label class="mb-club-minicart-v2__option">
                          <input type="radio" class="mb-club-minicart-v2__radio" name="${this.uid}-option" value="vip">
                          <span class="mb-club-minicart-v2__radio-dot" aria-hidden="true"></span>
                          <span class="mb-club-minicart-v2__option-text">M&amp;B Club</span>
                        </label>
                      </th>
                      <th scope="col" class="mb-club-minicart-v2__option-head" data-option="nonmember">
                        <label class="mb-club-minicart-v2__option">
                          <input type="radio" class="mb-club-minicart-v2__radio" name="${this.uid}-option" value="nonmember">
                          <span class="mb-club-minicart-v2__radio-dot" aria-hidden="true"></span>
                          <span class="mb-club-minicart-v2__option-text">Non-member</span>
                        </label>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    ${this.benefitRow('Member Discount', '', `<span class="mb-club-minicart-v2__value mb-club-minicart-v2__value--accent">${percent}%</span>`)}
                    ${this.benefitRow('Members Store Credit', '', `<span class="mb-club-minicart-v2__value mb-club-minicart-v2__value--accent">${price}/mo</span>`)}
                    ${this.benefitRow('1st Order Free Shipping', '', included)}
                    ${this.benefitRow('Members-only sales', '', included)}
                  </tbody>
                </table>
              </div>
              <p class="mb-club-minicart-v2__legal">By completing your purchase, you’ll be enrolled in the Mott &amp; Bow VIP Club. Membership is ${price} and is returned to you each month as store credit. Your first month is complimentary, with the flexibility to pause or cancel at any time. Plus, enjoy free shipping on your first order—on us.</p>
            </div>
          </div>
        </div>
        <button type="button" class="mb-club-minicart-v2__toggle" aria-controls="${this.uid}-benefits" aria-expanded="false">
          <span class="mb-club-minicart-v2__toggle-text">See all benefits</span>${MB_CLUB_MINICART_V2_CARET_ICON}
        </button>
      </section>
    `;
    this.isRendered = true;
    this.root = this.querySelector('.mb-club-minicart-v2');
    this.cta = this.querySelector('.mb-club-minicart-v2__cta');
    this.panel = this.querySelector('.mb-club-minicart-v2__panel');
    this.toggle = this.querySelector('.mb-club-minicart-v2__toggle');
    this.toggleText = this.querySelector('.mb-club-minicart-v2__toggle-text');
    this.table = this.querySelector('.mb-club-minicart-v2__table');
    this.radios = this.querySelectorAll('.mb-club-minicart-v2__radio');

    this.attachEventListeners();
    this.updateSelectedState();
    this.updateOpenState();

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'mb_club_widget_viewed',
      mb_club_subscription_added: this.isSubscriptionAdded,
      mb_club_widget_version: MB_CLUB_MINICART_V2_VERSION,
    });
  }
  attachEventListeners() {
    this.toggle.addEventListener('click', () => this.setOpen(!this.isOpen));

    this.cta.addEventListener('click', () => {
      if (this.isLoading) return;
      if (this.selectedOption === 'vip') {
        // Already added: the button opens the benefits table so the shopper can switch to Non-member.
        this.setOpen(!this.isOpen);
      } else {
        this.selectOption('vip');
      }
    });

    this.radios.forEach(radio => {
      radio.addEventListener('change', () => {
        if (radio.checked) this.selectOption(radio.value);
      });
    });

    // The whole column is clickable, not only its header radio.
    this.table.addEventListener('click', (event) => {
      if (event.target.closest('label')) return; // the header label already toggles its radio
      const cell = event.target.closest('[data-option]');
      if (cell) this.selectOption(cell.dataset.option);
    });
  }
  /**
   * Adds (VIP) or removes (Non-member) the membership, reverting the selection if the request fails.
   * @param {'vip'|'nonmember'} option - The column the shopper picked.
   */
  selectOption(option) {
    if (this.isLoading || option === this.selectedOption) {
      this.updateSelectedState();
      return;
    }
    const previousOption = this.selectedOption;
    this.selectedOption = option;
    this.updateSelectedState();
    this.setLoadingState(true);

    const request = option === 'vip'
      ? this.addSubscriptionProductToCart("subscriptionSelectorEventHandler").then(() => {
          localStorage.removeItem('mb_club_minicart_disabled');
        })
      : this.removeSubscriptionProductFromCart().then(() => {
          localStorage.setItem('mb_club_minicart_disabled', 'true');
        });

    request
      .then(() => this.refreshSideCart())
      .catch(error => {
        console.error(`Error ${option === 'vip' ? 'adding' : 'removing'} subscription product:`, error);
        // Revert to previous selection on error
        this.selectedOption = previousOption;
        this.updateSelectedState();
      })
      .finally(() => {
        this.setLoadingState(false);
      });
  }
  updateSelectedState() {
    if (!this.isRendered) return;
    const isVip = this.selectedOption === 'vip';
    this.root.dataset.selected = this.selectedOption;
    this.radios.forEach(radio => {
      radio.checked = radio.value === this.selectedOption;
    });

    this.cta.classList.toggle('mb-club-minicart-v2__cta--added', isVip);
    if (isVip) {
      this.cta.innerHTML = `${MB_CLUB_MINICART_V2_CHECK_ICON}<span class="visually-hidden">M&amp;B Club </span>Added<span class="visually-hidden">, show benefits</span>`;
      this.cta.setAttribute('aria-controls', this.panel.id);
      this.cta.setAttribute('aria-expanded', String(this.isOpen));
    } else {
      this.cta.textContent = 'Add M&B Club';
      this.cta.removeAttribute('aria-controls');
      this.cta.removeAttribute('aria-expanded');
    }
  }
  setOpen(isOpen) {
    this.isOpen = isOpen;
    // Save the open/closed state to localStorage
    localStorage.setItem('mb_club_minicart_v2_open', isOpen ? 'true' : 'false');
    this.updateOpenState();
  }
  updateOpenState() {
    if (!this.isRendered) return;
    // The --open class drives the CSS open/close animation (the panel stays in the DOM)
    this.root.classList.toggle('mb-club-minicart-v2--open', this.isOpen);
    this.toggle.setAttribute('aria-expanded', String(this.isOpen));
    this.toggleText.textContent = this.isOpen ? 'Hide benefits' : 'See all benefits';
    if (this.cta.hasAttribute('aria-controls')) {
      this.cta.setAttribute('aria-expanded', String(this.isOpen));
    }
  }
  setLoadingState(isLoading) {
    this.isLoading = isLoading;
    if (!this.isRendered) return;
    const loadingOverlay = this.querySelector('.mb-club-minicart-v2__loading-overlay');
    if (loadingOverlay) {
      loadingOverlay.classList.toggle('hidden', !isLoading);
    }
    this.querySelectorAll('.mb-club-minicart-v2__radio, .mb-club-minicart-v2__cta').forEach(control => {
      control.disabled = isLoading;
    });
    this.querySelector('.mb-club-minicart-v2')?.setAttribute('aria-busy', String(isLoading));
  }
  /**
   * Re-renders the side cart. Returns its promise so the loading state lasts until the cart is updated.
   */
  refreshSideCart() {
    const sideCart = document.querySelector('side-cart');
    if (sideCart && typeof sideCart.getCart === 'function') {
      return sideCart.getCart();
    }
  }
  /**
   * Hides the widget when the cart subtotal is 0 (same rule as V1).
   */
  updateVisibilityFromSubtotal() {
    const subtotal = parseFloat(this.getAttribute('data-subtotal'));
    this.classList.toggle('hidden', !subtotal);
    this.reportVisibility();
  }

  /**
   * Observe attribute changes for data-subtotal
   */
  static get observedAttributes() {
    return ['data-subtotal'];
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'data-subtotal' && newValue !== oldValue && this.isRendered) {
      this.updateVisibilityFromSubtotal();
    }
  }
}

function mbClubDefineMinicartV2() {
  // Exposed under the V1 name so side-cart.js (empty-cart path) works with either version.
  window.mbClubReportWidgetVisible = mbClubV2ReportWidgetVisible;
  customElements.define("mb-club-minicart-v2", MBClubMinicartV2);
}
// M&B Club A/B test: only the widget picked for this visitor is defined (mb-club-experiment.js).
window.mbClubExperiment?.whenWidget('v2', mbClubDefineMinicartV2);
